import {useMemo} from 'react';
import type {
  TaskAssignmentResponse,
  TaskDefinitionResponse,
} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {
  ActionPillConfig,
  AdminActionsMeta,
  UserActionsMeta,
} from 'src/app/actions/actionHelpers';
import {PENDING_SYNC_PILL} from 'src/app/tasks/taskHelpers';
import type {PillProps} from 'src/design/atoms/Pill';
import {isPendingSync} from 'src/offline/pendingSync';
import {useGetActionQuery} from 'src/redux/queries/action';
import {useGetComplianceStandardSelectOptionsQuery} from 'src/redux/queries/complianceStandard';
import {useTaskPermissions} from 'src/shared/auth/permissions/useTaskPermissions';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {createInitialFormStateV2} from 'src/types/FormV2Mapping';

export interface UseActionViewResult {
  viewItem: TaskDefinitionResponse | undefined;
  viewAssignment: TaskAssignmentResponse | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => Promise<void>;
  formMeta: IFormFieldV2[];
  formData: IFormDataV2;
  pills: PillProps[];
}

/** Read model for an action: Admin or User meta by view permission, form data and header pills. */
export const useActionView = (taskId: number | undefined): UseActionViewResult => {
  const taskPerms = useTaskPermissions(ModuleEnum.Actions);

  const {
    data: actionData,
    isLoading,
    error,
    refetch,
  } = useGetActionQuery(taskId!, {skip: taskId === undefined});

  const viewItem = actionData?.definition;
  const viewAssignment = actionData?.assignment;

  const {skipCompliance} = useSkipTokensByFeature();
  const {data: standards, isLoading: isStandardsLoading} =
    useGetComplianceStandardSelectOptionsQuery(undefined, {
      skip: skipCompliance,
    });

  const formMeta = useMemo<IFormFieldV2[]>(() => {
    if (viewItem === undefined || isStandardsLoading) {
      return [];
    }
    const useDueDate = true;
    const useDueInDays = false;
    const meta = taskPerms.view
      ? AdminActionsMeta(
          viewItem.state,
          standards?.data ?? [],
          useDueInDays,
          useDueDate
        )
      : UserActionsMeta(viewItem.state, useDueInDays, useDueDate);
    return meta.filter(field => field.name !== 'subject');
  }, [viewItem, isStandardsLoading, standards, taskPerms.view]);

  const formData = useMemo<IFormDataV2>(() => {
    if (!formMeta.length || !viewItem) {
      return {};
    }
    return {
      ...createInitialFormStateV2(formMeta),
      ...(viewItem as IFormDataV2),
      previewDueAt: viewItem.dueInDays ? viewItem.dueAt : undefined,
      inlineAttachments: viewItem.metadata?.inlineAttachments,
    };
  }, [formMeta, viewItem]);

  const pills = useMemo<PillProps[]>(
    () =>
      viewItem
        ? [
            ...(isPendingSync(viewAssignment) ? [PENDING_SYNC_PILL] : []),
            ...ActionPillConfig(
              viewItem.state,
              viewAssignment?.state,
              viewItem.participation,
              viewItem.dueAt
            ),
          ]
        : [],
    [viewItem, viewAssignment]
  );

  return {
    viewItem,
    viewAssignment,
    isLoading,
    error,
    refetch: async () => {
      await refetch();
    },
    formMeta,
    formData,
    pills,
  };
};
